import { AlertCircle, CheckCircle2, Loader2 } from "lucide-react";
import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useEnrollment } from "@/contexts/EnrollmentContext";
import { api } from "@/services/api";
import type { Payment } from "@/types";

export default function PaymentSuccessPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { refetch: refetchEnrollment } = useEnrollment();

  const [payment, setPayment] = useState<Payment | null>(null);
  const [isVerifying, setIsVerifying] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [attempts, setAttempts] = useState(0);

  const sessionId = searchParams.get("session_id");

  useEffect(() => {
    const maxAttempts = 10;
    const pollInterval = 2000; // 2 seconds

    const poll = async (attempt: number) => {
      if (!sessionId) {
        setError("Missing payment session ID");
        setIsVerifying(false);
        return;
      }

      if (attempt >= maxAttempts) {
        setError("Payment verification timed out. Please check your enrollments.");
        setIsVerifying(false);
        return;
      }

      try {
        const paymentData = await api.getPaymentBySession(sessionId);
        setPayment(paymentData);
        setAttempts(attempt + 1);

        if (paymentData.status === "completed") {
          // Payment completed! Refetch enrollment to update context
          await refetchEnrollment();
          setIsVerifying(false);
        } else if (paymentData.status === "failed" || paymentData.status === "cancelled") {
          setError(
            paymentData.error_message ||
              `Payment ${paymentData.status}. Please contact support if you were charged.`
          );
          setIsVerifying(false);
        } else {
          // Still pending, poll again
          setTimeout(() => poll(attempt + 1), pollInterval);
        }
      } catch (err) {
        if (attempt < maxAttempts - 1) {
          // Retry on error (payment might not be in DB yet)
          setTimeout(() => poll(attempt + 1), pollInterval);
        } else {
          setError(err instanceof Error ? err.message : "Failed to verify payment");
          setIsVerifying(false);
        }
      }
    };

    poll(0);
  }, [sessionId, refetchEnrollment]);

  if (!sessionId) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-900 via-teal-900 to-gray-900 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-xl p-8 max-w-md w-full text-center">
          <AlertCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Invalid Payment Link</h1>
          <p className="text-gray-600 mb-6">This payment link is invalid or expired.</p>
          <button
            onClick={() => navigate("/")}
            className="w-full bg-teal-600 hover:bg-teal-700 text-white font-medium py-3 px-6 rounded-lg transition-colors"
          >
            Go to Dashboard
          </button>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-900 via-teal-900 to-gray-900 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-xl p-8 max-w-md w-full text-center">
          <AlertCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Payment Verification Failed</h1>
          <p className="text-gray-600 mb-6">{error}</p>
          <div className="space-y-3">
            <button
              onClick={() => navigate("/")}
              className="w-full bg-teal-600 hover:bg-teal-700 text-white font-medium py-3 px-6 rounded-lg transition-colors"
            >
              Go to Dashboard
            </button>
            <button
              onClick={() => navigate("/register")}
              className="w-full bg-gray-100 hover:bg-gray-200 text-gray-700 font-medium py-3 px-6 rounded-lg transition-colors"
            >
              View Programmes
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (isVerifying) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-900 via-teal-900 to-gray-900 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-xl p-8 max-w-md w-full text-center">
          <Loader2 className="w-16 h-16 text-teal-600 mx-auto mb-4 animate-spin" />
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Verifying Payment</h1>
          <p className="text-gray-600 mb-4">Please wait while we confirm your payment...</p>
          {attempts > 0 && <p className="text-sm text-gray-500">Attempt {attempts} of 10</p>}
        </div>
      </div>
    );
  }

  // Payment completed successfully
  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-teal-900 to-gray-900 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-xl p-8 max-w-md w-full text-center">
        <CheckCircle2 className="w-16 h-16 text-green-500 mx-auto mb-4" />
        <h1 className="text-2xl font-bold text-gray-900 mb-2">Payment Successful!</h1>
        <p className="text-gray-600 mb-6">
          Your enrollment has been confirmed. Welcome to the programme!
        </p>

        {payment && (
          <div className="bg-gray-50 rounded-lg p-4 mb-6 text-left">
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-gray-600">Amount Paid:</span>
                <span className="font-medium text-gray-900">
                  ${(payment.amount / 100).toFixed(2)}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Payment Date:</span>
                <span className="font-medium text-gray-900">
                  {new Date(payment.completed_at || payment.created_at).toLocaleDateString()}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Transaction ID:</span>
                <span className="font-medium text-gray-900 text-xs truncate">
                  {payment.stripe_session_id.slice(-12)}
                </span>
              </div>
            </div>
          </div>
        )}

        <div className="space-y-3">
          <button
            onClick={async () => {
              if (payment) {
                try {
                  const { receipt_url } = await api.getPaymentReceiptUrl(payment.id);
                  window.open(receipt_url, "_blank");
                } catch (err) {
                  console.error("Failed to get receipt:", err);
                }
              }
            }}
            className="w-full bg-white/10 hover:bg-white/20 text-white font-medium py-3 px-6 rounded-lg transition-colors"
          >
            View Receipt
          </button>
          <button
            onClick={() => navigate("/")}
            className="w-full bg-teal-600 hover:bg-teal-700 text-white font-medium py-3 px-6 rounded-lg transition-colors"
          >
            Go to Dashboard
          </button>
        </div>
      </div>
    </div>
  );
}
